import React, { useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import { Bars3Icon } from "@heroicons/react/24/outline";
import ExcelExport from "../components/ExcelExport";
import TableHeaderFilter from "../components/TableHeaderfilter";
import MonthYearPicker from "../components/MonthYearPicker";
import axios from "axios";
import { SERVER_URL } from "../config";

const CURRENT_YEAR = new Date().getFullYear();

/* ── status badge ──────────────────────────────────────────────────────────── */
const StatusBadge = ({ status }) => {
    const map = {
        approved: "bg-emerald-50 text-emerald-700 border border-emerald-200",
        rejected:  "bg-rose-50 text-rose-700 border border-rose-200",
        pending:   "bg-amber-50 text-amber-700 border border-amber-200",
    };
    const key = status?.toLowerCase();
    return (
        <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${map[key] || "bg-slate-100 text-slate-600 border border-slate-200"}`}>
            {status}
        </span>
    );
};

/* ── tab style config ──────────────────────────────────────────────────────── */
const TAB_STYLES = {
    Pending:  { active: "bg-amber-500 text-white border-amber-500 shadow-sm",         idle: "text-slate-500 border-slate-200 bg-white hover:bg-amber-50 hover:text-amber-600 hover:border-amber-300" },
    Approved: { active: "bg-emerald-600 text-white border-emerald-600 shadow-sm",     idle: "text-slate-500 border-slate-200 bg-white hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-300" },
    Rejected: { active: "bg-rose-500 text-white border-rose-500 shadow-sm",           idle: "text-slate-500 border-slate-200 bg-white hover:bg-rose-50 hover:text-rose-600 hover:border-rose-300" },
};

/* ════════════════════════════════════════════════════════════════════════════ */
const LeaveApplications = () => {
    const [applications, setApplications]         = useState([]);
    const [search, setSearch]                     = useState("");
    const [filterStatus, setFilterStatus]         = useState("Pending");
    const [isModalOpen, setIsModalOpen]           = useState(false);
    const [selectedEmpStats, setSelectedEmpStats] = useState(null);
    const [loadingModal, setLoadingModal]         = useState(false);
    const [empIdFilter, setEmpIdFilter]           = useState([]);
    const [empNameFilter, setEmpNameFilter]       = useState([]);
    const [typeFilter, setTypeFilter]             = useState([]);
    const [sidebarOpen, setSidebarOpen]           = useState(false);
    const [pickedMonth, setPickedMonth]           = useState(null); // 1-12
    const [pickedYear, setPickedYear]             = useState(null);

    /* ---------- body scroll lock when modal open ---------- */
    useEffect(() => {
        document.body.style.overflow = isModalOpen ? "hidden" : "auto";
        return () => { document.body.style.overflow = "auto"; };
    }, [isModalOpen]);

    /* ---------- fetch all leaves ---------- */
    const fetchLeaveHistory = async () => {
        try {
            const response = await axios.get(`${SERVER_URL}/DataFetch/fetchAllLeaveApplication.php`);
            setApplications(response.data?.list ?? []);
        } catch (error) {
            console.error("Failed to fetch leave history", error);
        }
    };

    /* ---------- update status ---------- */
    const updateStatus = async (id, newStatus, adminId) => {
        try {
            const res = await axios.post(`${SERVER_URL}/Rahul/updateStatus.php`, {
                leave_id: id, status: newStatus, admin_id: adminId,
            });
            if (res.data?.status === "success") {
                setApplications((prev) =>
                    prev.map((app) => app.leave_id === id ? { ...app, status: newStatus } : app)
                );
            }
        } catch (err) {
            console.error("Error updating status:", err);
        }
    };

    const uniqueOptions = (data, key) =>
        [...new Set(data.map((d) => (d[key] ?? "").toString().trim()).filter(Boolean))]
            .sort().map((v) => ({ label: v, value: v }));

    const empIdOptions   = uniqueOptions(applications, "employee_id");
    const empNameOptions = uniqueOptions(applications, "employee_name");
    const typeOptions    = uniqueOptions(applications, "leave_name");

    /* ---------- row click → open popup ---------- */
    const handleRowClick = async (empId) => {
        setLoadingModal(true);
        try {
            const res = await axios.get(`${SERVER_URL}/Rahul/fetchAllEmployeeLeaveStat.php`, {
                params: { employee_id: empId, year: CURRENT_YEAR },
            });
            if (res.data?.status === true && res.data?.summary) {
                const { summary, history } = res.data;
                setSelectedEmpStats({
                    total_used:   Number(summary.total_used),
                    total_alloc:  Number(summary.total_casual) + Number(summary.total_sick),
                    casual_used:  Number(summary.used_casual),
                    casual_alloc: Number(summary.total_casual),
                    sick_used:    Number(summary.used_sick),
                    sick_alloc:   Number(summary.total_sick),
                    history:      history || [],
                });
                setIsModalOpen(true);
            } else {
                setSelectedEmpStats(null);
            }
        } catch (err) {
            console.error("Employee stats API failed", err);
            setSelectedEmpStats(null);
        } finally {
            setLoadingModal(false);
        }
    };

    useEffect(() => { fetchLeaveHistory(); }, []);

    /* ---------- filter + search ---------- */
    const filteredApplications = applications
        .filter((leave) => leave.status?.toLowerCase() === filterStatus.toLowerCase())
        .filter((leave) =>
            leave.employee_name?.toLowerCase().includes(search.toLowerCase()) ||
            leave.employee_id?.toLowerCase().includes(search.toLowerCase())
        )
        .filter((leave) => {
            // Month-year overlap: show if either start or end date falls in selected month,
            // or the leave spans across the entire selected month.
            if (pickedMonth !== null && pickedYear !== null) {
                const monthStart = new Date(pickedYear, pickedMonth - 1, 1);
                const monthEnd   = new Date(pickedYear, pickedMonth, 0, 23, 59, 59, 999);
                const leaveStart = new Date(leave.first_date);
                const leaveEnd   = new Date(leave.last_date);
                if (leaveStart > monthEnd || leaveEnd < monthStart) return false;
            }
            const empId   = (leave.employee_id  ?? "").toString();
            const empName = (leave.employee_name ?? "").toString();
            const type    = (leave.leave_name   ?? "").toString();
            return (
                (empIdFilter.length   === 0 || empIdFilter.includes(empId)) &&
                (empNameFilter.length === 0 || empNameFilter.includes(empName)) &&
                (typeFilter.length    === 0 || typeFilter.includes(type))
            );
        });

    const excelColumns = [
        { header: "Employee ID",   key: "employee_id",   type: "text" },
        { header: "Employee Name", key: "employee_name", type: "text" },
        { header: "From Date",     key: "first_date",    type: "text" },
        { header: "To Date",       key: "last_date",     type: "text" },
        { header: "Total Days",    key: "total_days",    type: "number" },
        { header: "Leave Type",    key: "leave_name",    type: "text" },
        { header: "Status",        key: "status",        type: "text" },
    ];

    /* ════════════════════════════════════════════════════════════════════════
       JSX
    ════════════════════════════════════════════════════════════════════════ */
    return (
        <>

            <div className="min-h-screen w-full font-[Inter]" style={{ background: "#f0f4f8" }}>

                {/* Subtle top accent line */}
                <div className="fixed top-0 left-0 right-0 h-0.5 z-50" style={{ background: "linear-gradient(90deg, #1e40af 0%, #0369a1 50%, #0e7490 100%)" }} />

                <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} onLogout={() => { localStorage.clear(); }} />

                {/* ── STICKY HEADER ── */}
                <div className="sticky top-0 z-20 border-b border-slate-200"
                    style={{ background: "#ffffff", boxShadow: "0 1px 4px rgba(15,23,42,0.08)" }}>
                    <div className="px-7 py-3.5 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => setSidebarOpen(!sidebarOpen)}
                                className="p-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors border-0"
                            >
                                <Bars3Icon className="h-5 w-5" />
                            </button>
                            <div className="flex items-center gap-2.5">
                                {/* Official document icon accent */}
                                <div className="w-1 h-6 rounded-full" style={{ background: "linear-gradient(180deg, #1e40af, #0369a1)" }} />
                                <h1 className="text-lg font-bold m-0 tracking-tight text-slate-800">
                                    Leave Applications
                                </h1>
                            </div>
                        </div>
                        <ExcelExport
                            data={filteredApplications}
                            columns={excelColumns}
                            fileName="LeaveReport.xlsx"
                            reportTitle="Leave Report"
                        />
                    </div>
                </div>

                {/* ── MAIN CONTENT ── */}
                <div className="relative z-10 flex flex-col gap-4 p-5">

                    {/* ── FILTER BAR ── */}
                    <div className="rounded-xl px-5 py-4 bg-white border border-slate-200"
                        style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}>
                        <div className="flex items-center justify-between gap-3 flex-wrap">

                            {/* Status tabs */}
                            <div className="flex items-center gap-2 flex-shrink-0 flex-nowrap">
                                {["Pending", "Approved", "Rejected"].map((s) => {
                                    const isActive = filterStatus === s;
                                    const st = TAB_STYLES[s];
                                    return (
                                        <button
                                            key={s}
                                            onClick={() => setFilterStatus(s)}
                                            className={`px-4 py-1.5 rounded-lg text-xs font-semibold border transition-all duration-200 whitespace-nowrap ${isActive ? st.active : st.idle}`}
                                        >
                                            {s}
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Right: MonthYearPicker + Search */}
                            <div className="flex items-center gap-3">
                                <MonthYearPicker
                                    selectedMonth={pickedMonth}
                                    selectedYear={pickedYear}
                                    onChange={({ month, year }) => { setPickedMonth(month); setPickedYear(year); }}
                                />
                                <input
                                    className="w-52 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 h-10 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors"
                                    placeholder="Search by Emp ID or Name"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>

                    {/* ── TABLE ── */}
                    <div className="rounded-xl overflow-hidden bg-white border border-slate-200"
                        style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}>
                        <div className="overflow-x-auto overflow-y-auto max-h-[520px] leave-scrollbar">
                            <table className="min-w-[700px] w-full text-sm">
                                <thead className="sticky top-0 z-10" style={{ background: "#f8fafc" }}>
                                    <tr>
                                        <th className="p-3 text-left border-b border-slate-200">
                                            <TableHeaderFilter label="Emp ID" options={empIdOptions} selectedValues={empIdFilter} onChange={setEmpIdFilter} />
                                        </th>
                                        <th className="p-3 text-left border-b border-slate-200">
                                            <TableHeaderFilter label="Name" options={empNameOptions} selectedValues={empNameFilter} onChange={setEmpNameFilter} />
                                        </th>
                                        <th className="p-3 text-left border-b border-slate-200">
                                            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">From</span>
                                        </th>
                                        <th className="p-3 text-left border-b border-slate-200">
                                            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">To</span>
                                        </th>
                                        <th className="p-3 text-center border-b border-slate-200">
                                            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Days</span>
                                        </th>
                                        <th className="p-3 text-left border-b border-slate-200">
                                            <TableHeaderFilter label="Type" options={typeOptions} selectedValues={typeFilter} onChange={setTypeFilter} />
                                        </th>
                                        <th className="p-3 text-center border-b border-slate-200">
                                            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Reason</span>
                                        </th>
                                        <th className="p-3 text-center border-b border-slate-200">
                                            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</span>
                                        </th>
                                        <th className="p-3 text-center border-b border-slate-200">
                                            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</span>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredApplications.length === 0 ? (
                                        <tr>
                                            <td colSpan={8} className="text-center p-8 text-slate-400 text-sm">
                                                No leave applications found.
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredApplications.map((leave) => (
                                            <tr
                                                key={leave.leave_id}
                                                onClick={() => handleRowClick(leave.employee_id)}
                                                className="border-t border-slate-100 cursor-pointer transition-colors hover:bg-blue-50/60"
                                            >
                                                <td className="p-3 text-slate-500 text-xs font-medium">{leave.employee_id}</td>
                                                <td className="p-3 text-slate-800 text-xs font-medium">{leave.employee_name}</td>
                                                <td className="p-3 text-slate-500 text-xs">{leave.first_date}</td>
                                                <td className="p-3 text-slate-500 text-xs">{leave.last_date}</td>
                                                <td className="p-3 text-center text-slate-700 text-xs font-bold">{leave.total_days}</td>
                                                <td className="p-3 text-slate-600 text-xs">{leave.leave_name}</td>
                                                <td className="p-3 text-slate-600 text-xs">{leave.reason}</td>
                                                <td className="p-3 text-center">
                                                    <StatusBadge status={leave.status} />
                                                </td>
                                                <td className="p-3 text-center" onClick={(e) => e.stopPropagation()}>
                                                    {leave.status?.toLowerCase() === "pending" ? (
                                                        <div className="flex justify-center gap-2">
                                                            <button
                                                                onClick={() => updateStatus(leave.leave_id, "Approved", leave.employee_id)}
                                                                className="px-3 py-1 rounded-lg text-xs font-semibold text-emerald-700 border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 transition-colors"
                                                            >
                                                                ✓ Approve
                                                            </button>
                                                            <button
                                                                onClick={() => updateStatus(leave.leave_id, "Rejected", leave.employee_id)}
                                                                className="px-3 py-1 rounded-lg text-xs font-semibold text-rose-700 border border-rose-200 bg-rose-50 hover:bg-rose-100 transition-colors"
                                                            >
                                                                ✕ Reject
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <span className="text-slate-300 text-xs">—</span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {/* ══════════════════════════════════════════════════════════════
                LEAVE HISTORY MODAL
            ══════════════════════════════════════════════════════════════ */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center px-4"
                    style={{ background: "rgba(15,23,42,0.45)", backdropFilter: "blur(6px)" }}>

                    <div className="relative flex flex-col rounded-2xl border border-slate-200 w-full max-w-sm bg-white"
                        style={{
                            boxShadow: "0 20px 60px rgba(15,23,42,0.18)",
                            maxHeight: "90vh",
                            padding: "24px",
                        }}>

                        {/* Modal header */}
                        <div className="flex-shrink-0 flex items-center justify-between pb-4 mb-4"
                            style={{ borderBottom: "1px solid #e2e8f0" }}>
                            <div className="flex items-center gap-2">
                                <div className="w-1 h-5 rounded-full bg-blue-700" />
                                <h3 className="text-base font-bold text-slate-800 m-0">
                                    Leave History
                                    <span className="ml-2 text-xs font-semibold text-slate-400">
                                        ({CURRENT_YEAR})
                                    </span>
                                </h3>
                            </div>
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-500 border border-slate-200 hover:bg-slate-100 transition-colors"
                            >
                                ✕
                            </button>
                        </div>

                        {/* Modal body */}
                        <div className="flex-1 overflow-y-auto leave-scrollbar">
                            {loadingModal ? (
                                <p className="text-center text-slate-400 text-sm py-6">Loading…</p>
                            ) : (
                                <>
                                    {/* Stat tiles */}
                                    <div className="grid grid-cols-3 gap-3 mb-5">
                                        {[
                                            { label: "Total",  used: selectedEmpStats?.total_used,  alloc: selectedEmpStats?.total_alloc,  color: "blue" },
                                            { label: "Casual", used: selectedEmpStats?.casual_used, alloc: selectedEmpStats?.casual_alloc, color: "amber" },
                                            { label: "Sick",   used: selectedEmpStats?.sick_used,   alloc: selectedEmpStats?.sick_alloc,   color: "rose" },
                                        ].map(({ label, used, alloc, color }) => {
                                            const themes = {
                                                blue:  { bg: "bg-blue-50 border border-blue-100",   accent: "text-blue-500",  val: "text-blue-800" },
                                                amber: { bg: "bg-amber-50 border border-amber-100", accent: "text-amber-500", val: "text-amber-800" },
                                                rose:  { bg: "bg-rose-50 border border-rose-100",   accent: "text-rose-500",  val: "text-rose-800" },
                                            };
                                            const t = themes[color];
                                            return (
                                                <div key={label} className={`${t.bg} rounded-xl px-3 py-3 text-center`}>
                                                    <p className={`text-[10px] font-bold uppercase tracking-widest ${t.accent}`}>{label}</p>
                                                    <p className={`text-lg font-extrabold ${t.val}`}>{used}/{alloc}</p>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {/* History table */}
                                    <div className="rounded-xl overflow-hidden border border-slate-200">
                                        <div className="overflow-y-auto max-h-[220px] leave-scrollbar">
                                            <table className="w-full text-xs">
                                                <thead className="sticky top-0 z-10" style={{ background: "#f8fafc" }}>
                                                    <tr>
                                                        {["Dates", "Days", "Type", "Status"].map((h) => (
                                                            <th key={h} className="p-2 border-b border-slate-200 text-center">
                                                                <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">{h}</span>
                                                            </th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {selectedEmpStats?.history?.length > 0 ? (
                                                        selectedEmpStats.history.map((h, i) => (
                                                            <tr key={i} className="border-t border-slate-100 hover:bg-slate-50 transition-colors">
                                                                <td className="p-2 text-slate-500 text-center whitespace-nowrap">
                                                                    {h.start_date} – {h.end_date}
                                                                </td>
                                                                <td className="p-2 text-slate-700 text-center font-bold">{h.total_days}</td>
                                                                <td className="p-2 text-slate-600 text-center">{h.leave_name}</td>
                                                                <td className="p-2 text-center"><StatusBadge status={h.status} /></td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr>
                                                            <td colSpan={4} className="p-4 text-center text-slate-400">No leave history found</td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="flex-shrink-0 flex justify-end mt-4 pt-3"
                            style={{ borderTop: "1px solid #e2e8f0" }}>
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="px-5 py-2 rounded-xl text-sm font-semibold text-slate-600 border border-slate-200 hover:bg-slate-100 transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default LeaveApplications;